/**
 * Employee accounts (Phase 2). `admin` and `hr`, enforced on the server.
 *
 * ## A copy of the back office's screen, and the copy is known
 *
 * This is `frontend/back-office/src/pages/UsersPage.tsx` from nab-agent, unchanged. It talks to
 * the same `app/api/users.py` over the same `@nab/shared` client against the same `users` table —
 * what differs is only which application an administrator opened. ADR 0028.
 *
 * `scripts/sync-platform.py` does not cover `frontend/`, so unlike the vendored Python this copy
 * is not hash-checked and nothing will complain when the two drift. A change to either belongs in
 * both until account administration is lifted into `@nab/shared` as a shared page, which is where
 * it should end up.
 *
 * ## The temporary password is shown once and then gone
 *
 * Creating an account and forcing a reset both return a password the administrator has to pass to the
 * employee out of band. It exists in this component's state and nowhere else — not in the audit
 * payload, not in a log, not behind a second endpoint that could fetch it later. So it stays on
 * screen until dismissed deliberately, with a line saying it cannot be shown again, because an
 * administrator who closes it by reflex has to force another reset.
 *
 * ## A 403 is a sentence, not a blank screen
 *
 * A compliance officer who reaches this screen sees the server's refusal explained. The alternative —
 * hiding the screen and showing nothing — makes "you do not have this role" indistinguishable from
 * "the back office is broken", and the second is what gets reported.
 *
 * The same holds for an HR officer acting on an administrator's account: the refusal names the
 * reason rather than failing blankly. What is *not* offered is the `admin` role in the pickers —
 * see `grantableBy`.
 *
 * ## Suspension asks first
 *
 * It ends every one of that employee's sessions immediately, so it is the one action here that
 * interrupts someone mid-task. Confirmation names the person, because the confirmation an
 * administrator is going to click through anyway should at least tell them who it is about.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ApiError,
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  Select,
  approveUser,
  StatusPill,
  Pager,
  Table,
  createUser,
  fetchUsers,
  formatTimestamp,
  reactivateUser,
  resetUserPassword,
  setUserRole,
  suspendUser,
  updateUser,
  useSession,
  useToast,
  type Column,
  type CreatedUserAccount,
  type Role,
  type StatusTone,
  type UserAccount,
} from "@nab/shared";

const ROLES: Role[] = ["agent", "admin", "compliance", "hr"];

/** Roles only an administrator can grant. Mirrors `PRIVILEGED_ROLES` in `app/roles.py`. */
const PRIVILEGED = new Set<Role>(["admin", "hr"]);

/**
 * The roles the signed-in employee may grant.
 *
 * An HR officer may not create or promote to `admin` or `hr`: the server refuses it in
 * `_guard_grant` (app/api/users.py), because account administration is the only screen that role
 * can open and granting itself `admin` would turn it into all of them.
 *
 * This is the one place the application hides a control rather than letting the server explain the
 * refusal. The difference is that a select option is a choice being offered, and an option that
 * can only ever fail is a defect rather than an explanation. The filter below still lists every
 * role — an HR officer may *look at* who the administrators are; they just cannot make one.
 */
function grantableBy(role: Role | undefined): Role[] {
  return role === "admin" ? ROLES : ROLES.filter((candidate) => !PRIVILEGED.has(candidate));
}
const LANGUAGES = ["en", "ar"] as const;

/** Account state to a meaning. `locked` is a warning, not a failure: it clears on its own. */
function toneFor(user: UserAccount): StatusTone {
  // Waiting, not wrong. A registration request is an ordinary state and colouring it as a warning
  // is how administrators learn to ignore warnings.
  if (user.status === "pending") return "info";
  if (user.status === "suspended") return "danger";
  if (user.locked) return "warning";
  if (user.must_change_password) return "info";
  return "success";
}

interface DraftUser {
  employee_id: string;
  email: string;
  full_name: string;
  role: Role;
  preferred_language: "ar" | "en";
}

const EMPTY_DRAFT: DraftUser = {
  employee_id: "",
  email: "",
  full_name: "",
  role: "agent",
  preferred_language: "en",
};

/** Rows per page. The API's own default, so the first page is what this screen always showed. */
const PAGE_SIZE = 100;

export function UsersPage(): JSX.Element {
  const { t } = useTranslation("app");
  const toast = useToast();
  const { identity } = useSession();
  const grantable = grantableBy(identity?.role);

  const [users, setUsers] = useState<UserAccount[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [roleFilter, setRoleFilter] = useState<"" | Role>("");

  const [editing, setEditing] = useState<UserAccount | null>(null);
  const [draft, setDraft] = useState<DraftUser>(EMPTY_DRAFT);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [confirmSuspend, setConfirmSuspend] = useState<UserAccount | null>(null);
  const [approving, setApproving] = useState<UserAccount | null>(null);
  const [approveRole, setApproveRole] = useState<Role>("agent");
  const [issued, setIssued] = useState<CreatedUserAccount | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchUsers({
        limit: PAGE_SIZE,
        offset,
        ...(roleFilter ? { role: roleFilter } : {}),
      });
      setUsers(result.users);
      setTotal(result.total);
      setForbidden(false);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 403) {
        setForbidden(true);
      } else {
        toast.show({ tone: "error", message: t("users.toast.loadFailed"), ...detailOf(caught) });
      }
    } finally {
      setLoading(false);
    }
  }, [roleFilter, offset, t, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // Changing the role filter goes back to the first page, for the same reason the knowledge base
  // does it on a new search: page four of "every account" is not page four of "the three HR
  // officers", and the table would render empty.
  useEffect(() => {
    setOffset(0);
  }, [roleFilter]);

  function openCreate() {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setFormOpen(true);
  }

  function openEdit(user: UserAccount) {
    setEditing(user);
    setDraft({
      employee_id: user.employee_id,
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      preferred_language: user.preferred_language,
    });
    setFormOpen(true);
  }

  async function save() {
    setSaving(true);
    try {
      if (editing) {
        await updateUser(editing.user_id, {
          email: draft.email,
          full_name: draft.full_name,
          preferred_language: draft.preferred_language,
        });
        // A separate endpoint, because "who made this person an administrator" is a question the
        // audit trail has to answer on its own, not as a field inside a profile edit.
        if (draft.role !== editing.role) {
          await setUserRole(editing.user_id, draft.role);
        }
        toast.show({ tone: "success", message: t("users.toast.updated", { name: draft.full_name }) });
      } else {
        const created = await createUser(draft);
        setIssued(created);
        toast.show({ tone: "success", message: t("users.toast.created", { name: draft.full_name }) });
      }
      setFormOpen(false);
      await load();
    } catch (caught) {
      toast.show({ tone: "error", message: t("users.toast.saveFailed"), ...detailOf(caught) });
    } finally {
      setSaving(false);
    }
  }

  async function doSuspend(user: UserAccount) {
    try {
      await suspendUser(user.user_id);
      toast.show({
        tone: "warning",
        message: t("users.toast.suspended", { name: user.full_name }),
        detail: t("users.toast.suspendedDetail"),
      });
      await load();
    } catch (caught) {
      toast.show({ tone: "error", message: t("users.toast.suspendFailed"), ...detailOf(caught) });
    } finally {
      setConfirmSuspend(null);
    }
  }

  async function doApprove(user: UserAccount, role: Role) {
    try {
      await approveUser(user.user_id, role);
      toast.show({
        tone: "success",
        message: t("users.toast.approved", { name: user.full_name }),
        detail: t(`users.role.${role}`),
      });
      await load();
    } catch (caught) {
      toast.show({ tone: "error", message: t("users.toast.approveFailed"), ...detailOf(caught) });
    } finally {
      setApproving(null);
      // Back to the least-privileged option. A dialog that remembers "admin" from the last
      // approval is one mis-click away from granting it to the next person in the queue.
      setApproveRole("agent");
    }
  }

  async function doReactivate(user: UserAccount) {
    try {
      await reactivateUser(user.user_id);
      toast.show({ tone: "success", message: t("users.toast.reactivated", { name: user.full_name }) });
      await load();
    } catch (caught) {
      toast.show({ tone: "error", message: t("users.toast.reactivateFailed"), ...detailOf(caught) });
    }
  }

  async function doReset(user: UserAccount) {
    try {
      const result = await resetUserPassword(user.user_id);
      setIssued(result);
      toast.show({
        tone: "info",
        message: t("users.toast.resetIssued", { name: user.full_name }),
        detail: t("users.toast.resetIssuedDetail"),
      });
      await load();
    } catch (caught) {
      toast.show({ tone: "error", message: t("users.toast.resetFailed"), ...detailOf(caught) });
    }
  }

  const columns: Column<UserAccount>[] = [
    {
      key: "name",
      header: t("users.column.name"),
      render: (user) => (
        <div className="flex flex-col">
          <span className="text-label-md text-textPrimary">
            <bdi>{user.full_name}</bdi>
          </span>
          {/* Identifiers are Latin regardless of interface language, so they are forced LTR
              rather than inheriting the paragraph direction and reading backwards in Arabic. */}
          <span className="text-label-sm text-textMuted" dir="ltr">
            {user.employee_id}
          </span>
        </div>
      ),
    },
    {
      key: "email",
      header: t("users.column.email"),
      render: (user) => (
        <span className="text-body-sm text-textMuted" dir="ltr">
          {user.email}
        </span>
      ),
    },
    {
      key: "role",
      header: t("users.column.role"),
      render: (user) => (
        <span className="text-body-sm text-textPrimary">{t(`users.role.${user.role}`)}</span>
      ),
    },
    {
      key: "status",
      header: t("users.column.status"),
      render: (user) => (
        <StatusPill tone={toneFor(user)}>
          {user.status === "pending"
            ? t("users.status.pendingApproval")
            : user.status === "suspended"
            ? t("users.status.suspended")
            : user.locked
              ? t("users.status.locked")
              : user.must_change_password
                ? t("users.status.pendingPassword")
                : t("users.status.active")}
        </StatusPill>
      ),
    },
    {
      key: "lastLogin",
      header: t("users.column.lastLogin"),
      render: (user) => (
        // `dir="ltr"` around a format that carries no direction marks of its own — see
        // `formatTimestamp`. Both halves are needed: the format keeps the parts in order, the
        // attribute keeps the surrounding Arabic text from pulling the whole run around.
        <span className="text-body-sm text-textMuted" dir="ltr">
          {formatTimestamp(user.last_login_at, t("users.status.neverSignedIn"))}
        </span>
      ),
    },
    {
      key: "actions",
      header: t("users.column.actions"),
      render: (user) => {
        // The server refuses this too — see `suspend` in app/api/users.py. Disabling it here just
        // saves the administrator from clicking into a 400 that tells them what they already know.
        const isSelf = user.employee_id === identity?.employee_id;
        // A registration request is not an account yet: the only decisions are admitting it with a
        // role, or refusing it. Editing or resetting the password of something nobody has agreed
        // to admit would be doing administration on a stranger.
        if (user.status === "pending") {
          return (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => setApproving(user)}
              >
                {t("users.action.approve")}
              </Button>
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={() => setConfirmSuspend(user)}
              >
                {t("users.action.reject")}
              </Button>
            </div>
          );
        }

        return (
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => openEdit(user)}>
              {t("users.action.edit")}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void doReset(user)}
            >
              {t("users.action.resetPassword")}
            </Button>
            {user.status === "active" ? (
              <Button
                type="button"
                variant="danger"
                size="sm"
                disabled={isSelf}
                title={isSelf ? t("users.action.cannotSuspendSelf") : undefined}
                onClick={() => setConfirmSuspend(user)}
              >
                {t("users.action.suspend")}
              </Button>
            ) : (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => void doReactivate(user)}
              >
                {t("users.action.reactivate")}
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  if (forbidden) {
    return (
      <Card>
        <EmptyState
          title={t("users.forbidden.title")}
          description={t("users.forbidden.description")}
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-heading-md text-textPrimary">{t("users.heading")}</h2>
            <p className="mt-1 text-body-sm text-textMuted">{t("users.subheading")}</p>
          </div>
          <div className="flex items-end gap-3">
            <Select
              label={t("users.filter.role")}
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value as "" | Role)}
              options={[
                { value: "", label: t("users.filter.allRoles") },
                ...ROLES.map((role) => ({ value: role, label: t(`users.role.${role}`) })),
              ]}
            />
            <Button type="button" variant="primary" onClick={openCreate}>
              {t("users.action.create")}
            </Button>
          </div>
        </div>
      </Card>

      {issued ? (
        <Card className="border-statusInfo bg-statusInfoSubtle">
          <h2 className="text-heading-sm text-textPrimary">{t("users.issued.heading")}</h2>
          <p className="mt-1 text-body-sm text-textPrimary">
            {t("users.issued.forEmployee")} <bdi>{issued.user.full_name}</bdi>
          </p>
          <p
            className="mt-3 select-all rounded-sm border border-borderSubtle bg-surface px-3 py-2 font-mono text-body-md text-textPrimary"
            dir="ltr"
          >
            {issued.temporary_password}
          </p>
          {/* Composed here, not sent by the server: an English sentence rendered into an Arabic
              screen is what this replaced. The server supplies the minimum length and whether
              sessions were ended; the wording is in locales/{ar,en}.json. */}
          <p className="mt-2 text-body-sm text-textMuted">
            {t("users.issued.rule", { count: issued.min_password_length })}
            {" "}
            {issued.sessions_ended
              ? t("users.issued.sessionsEnded")
              : t("users.issued.mustChangeAtFirstSignIn")}
          </p>
          <p className="mt-1 text-body-sm text-statusWarning">{t("users.issued.shownOnce")}</p>
          <div className="mt-4">
            <Button type="button" variant="secondary" onClick={() => setIssued(null)}>
              {t("users.issued.dismiss")}
            </Button>
          </div>
        </Card>
      ) : null}

      <Card>
        {loading ? (
          <p role="status" aria-live="polite" className="text-body-sm text-textMuted">
            {t("users.loading")}
          </p>
        ) : (
          <Table
            caption={t("users.table.caption")}
            columns={columns}
            rows={users}
            rowKey={(user) => user.user_id}
            empty={
              <EmptyState title={t("users.empty.title")} description={t("users.empty.description")} />
            }
          />
        )}
        <div className="pt-3">
          <Pager
            total={total}
            limit={PAGE_SIZE}
            offset={offset}
            onChange={setOffset}
            busy={loading}
            label={t("users.pager.label")}
            previousLabel={t("users.pager.previous")}
            nextLabel={t("users.pager.next")}
            summary={t("users.pager.summary", {
              from: offset + 1,
              to: Math.min(offset + PAGE_SIZE, total),
              total,
            })}
          />
        </div>
      </Card>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? t("users.form.editTitle") : t("users.form.createTitle")}
        closeLabel={t("users.form.close")}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setFormOpen(false)}>
              {t("users.form.cancel")}
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={saving || !draft.employee_id || !draft.email || !draft.full_name}
              onClick={() => void save()}
            >
              {saving ? t("users.form.saving") : t("users.form.save")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Input
            label={t("users.form.employeeId")}
            value={draft.employee_id}
            onChange={(event) => setDraft({ ...draft, employee_id: event.target.value })}
            // Immutable after creation: it is the actor id in every audit event already written,
            // and changing it would orphan that history.
            readOnly={editing !== null}
            {...(editing ? { hint: t("users.form.employeeIdFixed") } : {})}
            dir="ltr"
            required
          />
          <Input
            label={t("users.form.fullName")}
            value={draft.full_name}
            onChange={(event) => setDraft({ ...draft, full_name: event.target.value })}
            required
          />
          <Input
            label={t("users.form.email")}
            type="email"
            value={draft.email}
            onChange={(event) => setDraft({ ...draft, email: event.target.value })}
            dir="ltr"
            required
          />
          <Select
            label={t("users.form.role")}
            value={draft.role}
            onChange={(event) => setDraft({ ...draft, role: event.target.value as Role })}
            options={grantable.map((role) => ({ value: role, label: t(`users.role.${role}`) }))}
          />
          <Select
            label={t("users.form.language")}
            value={draft.preferred_language}
            onChange={(event) =>
              setDraft({ ...draft, preferred_language: event.target.value as "ar" | "en" })
            }
            options={LANGUAGES.map((language) => ({
              value: language,
              label: t(`users.language.${language}`),
            }))}
          />
          {editing ? null : (
            <p className="text-body-sm text-textMuted">{t("users.form.temporaryPasswordNote")}</p>
          )}
        </div>
      </Modal>

      {/* Approval and role are one dialog because they are one decision. An approved account with
          no decided role would be live and unclassified in the window between two clicks. */}
      <Modal
        open={approving !== null}
        onClose={() => setApproving(null)}
        title={t("users.confirm.approveTitle")}
        closeLabel={t("users.form.close")}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setApproving(null)}>
              {t("users.form.cancel")}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => approving && void doApprove(approving, approveRole)}
            >
              {t("users.action.approve")}
            </Button>
          </>
        }
      >
        <p className="text-body-md text-textPrimary">
          {t("users.confirm.approveBody")} <bdi>{approving?.full_name}</bdi>
        </p>
        <p className="mt-1 text-body-sm text-textMuted" dir="ltr">
          {approving?.employee_id} — {approving?.email}
        </p>
        <div className="mt-4">
          <Select
            label={t("users.form.role")}
            value={approveRole}
            onChange={(event) => setApproveRole(event.target.value as Role)}
            options={grantable.map((role) => ({ value: role, label: t(`users.role.${role}`) }))}
          />
        </div>
        <p className="mt-2 text-body-sm text-textMuted">{t("users.confirm.approveConsequence")}</p>
      </Modal>

      <Modal
        open={confirmSuspend !== null}
        onClose={() => setConfirmSuspend(null)}
        title={t("users.confirm.suspendTitle")}
        closeLabel={t("users.form.close")}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setConfirmSuspend(null)}>
              {t("users.form.cancel")}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => confirmSuspend && void doSuspend(confirmSuspend)}
            >
              {t("users.action.suspend")}
            </Button>
          </>
        }
      >
        <p className="text-body-md text-textPrimary">
          {t("users.confirm.suspendBody")} <bdi>{confirmSuspend?.full_name}</bdi>
        </p>
        <p className="mt-2 text-body-sm text-textMuted">{t("users.confirm.suspendConsequence")}</p>
      </Modal>
    </div>
  );
}

/** The server's reason, when it sent one worth showing. */
function detailOf(caught: unknown): { detail?: string } {
  if (caught instanceof ApiError && typeof caught.detail === "string") {
    return { detail: caught.detail };
  }
  return {};
}
